import { Suspense, lazy } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './shell/AppShell';
import { RequireAuth } from '@/features/auth/RequireAuth';

/**
 * Route tree.
 *
 * Every module is lazily loaded at its route boundary. The map and chart
 * libraries alone are most of the bundle, and a technician opening their job
 * list on a phone over LTE should not download the GIS module to do it.
 */
const NocDashboard = lazy(() =>
  import('@/features/noc/routes/NocDashboard').then((m) => ({ default: m.NocDashboard })),
);
const Login = lazy(() => import('@/features/auth/routes/Login').then((m) => ({ default: m.Login })));
const MfaEnrolment = lazy(() =>
  import('@/features/auth/routes/MfaEnrolment').then((m) => ({ default: m.MfaEnrolment })),
);
const Devices = lazy(() =>
  import('@/features/monitoring/routes/Devices').then((m) => ({ default: m.Devices })),
);
const Alarms = lazy(() =>
  import('@/features/alarms/routes/Alarms').then((m) => ({ default: m.Alarms })),
);
const Incidents = lazy(() =>
  import('@/features/incidents/routes/Incidents').then((m) => ({ default: m.Incidents })),
);
const Plant = lazy(() =>
  import('@/features/plant/routes/Plant').then((m) => ({ default: m.Plant })),
);
const FieldOps = lazy(() =>
  import('@/features/field/routes/FieldOps').then((m) => ({ default: m.FieldOps })),
);
const Analytics = lazy(() =>
  import('@/features/analytics/routes/Analytics').then((m) => ({ default: m.Analytics })),
);
const Subscribers = lazy(() =>
  import('@/features/subscribers/routes/Subscribers').then((m) => ({ default: m.Subscribers })),
);
const Users = lazy(() =>
  import('@/features/admin/routes/Users').then((m) => ({ default: m.Users })),
);
const Sites = lazy(() =>
  import('@/features/sites/routes/Sites').then((m) => ({ default: m.Sites })),
);
const Notifications = lazy(() =>
  import('@/features/admin/routes/Notifications').then((m) => ({ default: m.Notifications })),
);
const AuditLog = lazy(() =>
  import('@/features/admin/routes/AuditLog').then((m) => ({ default: m.AuditLog })),
);
const IncidentDetail = lazy(() =>
  import('@/features/incidents/routes/IncidentDetail').then((m) => ({ default: m.IncidentDetail })),
);

export function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />

        {/* Outside the shell: it is a gate, not a destination. RequireAuth
            sends every MFA-mandatory account here until enrolment completes,
            so this route existing is what stops that becoming a loop. */}
        <Route
          path="/settings/mfa"
          element={
            <RequireAuth>
              <MfaEnrolment />
            </RequireAuth>
          }
        />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/noc" replace />} />
          <Route path="/noc" element={<NocDashboard />} />
          <Route path="/alarms" element={<Alarms />} />
          <Route path="/incidents" element={<Incidents />} />
          <Route path="/incidents/:id" element={<IncidentDetail />} />
          <Route path="/monitoring" element={<Devices />} />
          <Route path="/subscribers" element={<Subscribers />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/field" element={<FieldOps />} />
          <Route path="/gis" element={<Plant />} />
          <Route path="/sites" element={<Sites />} />
          <Route path="/admin" element={<Users />} />
          <Route path="/admin/audit" element={<AuditLog />} />
          <Route path="/admin/notifications" element={<Notifications />} />

          {/* Remaining module routes are added as each phase lands — see
              docs/07-roadmap.md. They are deliberately absent rather than
              stubbed: a nav item that leads to an empty page teaches an
              operator that the tool is unreliable. */}
        </Route>

        {/* A real not-found page, not a redirect.
            Bouncing unknown paths to /noc hid a genuine fault: RequireAuth
            redirected to /settings/mfa, which did not exist, so the catch-all
            sent it back and the two ping-ponged forever. A 404 fails visibly
            instead of looping. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function RouteFallback() {
  return (
    <div
      className="flex h-full items-center justify-center text-sm text-muted"
      role="status"
      aria-live="polite"
    >
      Loading…
    </div>
  );
}

function NotFound() {
  return (
    <main className="flex min-h-full flex-col items-center justify-center gap-3 bg-base p-4 text-center">
      <p data-metric className="text-3xl font-semibold text-muted">
        404
      </p>
      <p className="text-sm text-secondary">
        That page does not exist. Most modules are not built yet — see the sidebar for what is.
      </p>
      <Link
        to="/noc"
        className="mt-2 rounded-md px-3 py-2 text-sm font-medium"
        style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}
      >
        Back to the NOC dashboard
      </Link>
    </main>
  );
}
